import { Link } from 'react-router-dom';
import { IconChevronLeft } from '../icons';
import { CodeBoxMark } from '../BrandLogo';
import { ThemeToggle } from '../ThemeToggle';
import type { Device } from './DeviceFrame';

/**
 * The 56px studio toolbar — ported from the old Code Box `StudioToolbarV3`.
 *
 * Layout is deliberate: identity + title on the left, the DEVICE group centered (it is the only
 * sizing control users get), and every action on the right ending in the single forward CTA.
 * Frosted so the preview scrolls under it.
 */
interface StudioToolbarProps {
  title: string;
  stageLabel: string;
  device: Device;
  onDevice: (device: Device) => void;
  onReload: () => void;
  onFullscreen: () => void;
  published: boolean;
  onPublish: () => void;
  right?: React.ReactNode;
  /** 35i #5: main undo/redo (walks the version timeline) */
  onUndo?: () => void;
  onRedo?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;
  /** 35i #5: collaborators control (rendered by the parent) */
  collaborators?: React.ReactNode;
}

/* device icons — ported from Sparked v2's studio so the two platforms match */
const MonitorIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8M12 17v4" /></svg>
);
const TabletIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="2" /><path d="M12 18h.01" /></svg>
);
const PhoneIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="7" y="2" width="10" height="20" rx="2" /><path d="M11 18h2" /></svg>
);
const UndoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 14 4 9l5-5" /><path d="M4 9h11a5 5 0 0 1 0 10h-3" /></svg>
);
const RedoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 14 5-5-5-5" /><path d="M20 9H9a5 5 0 0 0 0 10h3" /></svg>
);
const DEVICES: ReadonlyArray<{ value: Device; label: string; icon: React.ReactNode }> = [
  { value: 'desktop', label: 'Desktop preview', icon: <MonitorIcon /> },
  { value: 'tablet', label: 'Tablet preview', icon: <TabletIcon /> },
  { value: 'mobile', label: 'Mobile preview', icon: <PhoneIcon /> },
];

export function StudioToolbar({
  title,
  stageLabel,
  device,
  onDevice,
  onReload,
  onFullscreen,
  published,
  onPublish,
  right,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  collaborators,
}: StudioToolbarProps) {
  return (
    /*
      Restyled to match the build screens: no hard bottom border, one soft surface, quieter type.
      The chrome should recede — the app being built is the subject, and every line of framing was
      competing with it.
    */
    <header className="sticky top-0 z-20 flex h-16 items-center gap-3 bg-surface/80 px-4 backdrop-blur-md">
      {/* left: identity + project */}
      <div className="flex min-w-0 flex-1 items-center gap-2.5">
        <Link
          to="/projects"
          title="All projects"
          aria-label="All projects"
          className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-muted transition hover:bg-page hover:text-ink"
        >
          <IconChevronLeft width={16} height={16} />
        </Link>
        <span className="hidden sm:block">
          <CodeBoxMark className="h-6 w-auto" />
        </span>
        <div className="min-w-0">
          <p className="max-w-[280px] truncate text-sm font-bold leading-tight">{title || 'Untitled'}</p>
          <p className="text-[11px] font-medium text-muted">{stageLabel}</p>
        </div>
      </div>

      {/* center: undo/redo + device toggle in ONE soft group */}
      <div className="hidden items-center gap-1 rounded-xl bg-page/70 p-1 lg:flex">
        <button
          type="button"
          onClick={onUndo}
          disabled={!canUndo}
          title="Undo"
          aria-label="Undo"
          className="grid h-8 w-8 place-items-center rounded-lg text-muted transition hover:bg-surface hover:text-ink disabled:opacity-30"
        >
          <UndoIcon />
        </button>
        <button
          type="button"
          onClick={onRedo}
          disabled={!canRedo}
          title="Redo"
          aria-label="Redo"
          className="grid h-8 w-8 place-items-center rounded-lg text-muted transition hover:bg-surface hover:text-ink disabled:opacity-30"
        >
          <RedoIcon />
        </button>
        <span className="mx-0.5 h-5 w-px bg-ink/10" />
        {DEVICES.map(d => (
          <button
            key={d.value}
            type="button"
            onClick={() => onDevice(d.value)}
            title={d.label}
            aria-label={d.label}
            className={['grid h-8 w-8 place-items-center rounded-lg transition', device === d.value ? 'bg-surface text-accent-deep shadow-sm' : 'text-muted hover:text-ink'].join(' ')}
          >
            {d.icon}
          </button>
        ))}
      </div>

      {/* right: actions, ending in the single forward CTA */}
      <div className="flex flex-1 items-center justify-end gap-1">
        {right}
        {collaborators}
        <ThemeToggle />
        <button
          type="button"
          onClick={onReload}
          title="Reload the preview"
          aria-label="Reload the preview"
          className="grid h-9 w-9 place-items-center rounded-lg text-muted transition hover:bg-page hover:text-ink"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 11A8 8 0 1 0 12 20" />
            <path d="M20 5v6h-6" />
          </svg>
        </button>
        <button
          type="button"
          onClick={onFullscreen}
          title="Full screen"
          aria-label="Full screen"
          className="grid h-9 w-9 place-items-center rounded-lg text-muted transition hover:bg-page hover:text-ink"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <path d="M8 3H3v5M16 3h5v5M8 21H3v-5M16 21h5v-5" />
          </svg>
        </button>
        <button
          type="button"
          onClick={onPublish}
          // the ONE forward CTA wears the brand yellow — everything else in the chrome stays blue
          className="ml-1.5 inline-flex h-9 items-center justify-center rounded-lg bg-sun px-5 text-[13px] font-extrabold text-brand transition hover:brightness-105"
        >
          {published ? 'Share' : 'Publish'}
        </button>
      </div>
    </header>
  );
}
